import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DISCUSS_HUB_WS_URL, DISCUSS_HUB_HTTP_URL } from '../../config/serviceConfig';
import EnhancedMarkdown from '../enhanced/EnhancedMarkdown';
import { buildAccentThemeStyle } from '../../utils/brandTheme';
import { getPreferredLanguage } from '../../config/languages';
import './DiscussionPanel.css';

/**
 * 研讨厅面板组件
 * 独立WebSocket连接到DiscussHub(:8010)
 */
const DiscussionPanel = ({
  isOpen,
  onClose,
  currentUser,
  // 状态props
  sessionId, setSessionId,
  phase, setPhase,
  panelists, setPanelists,
  messages, setMessages,
  topicSummary, setTopicSummary,
  isWaiting, setIsWaiting,
  status, setStatus,
  wsRef,
  onReset,
}) => {
  const { t } = useTranslation();
  const [topicInput, setTopicInput] = useState('');
  const [topicType, setTopicType] = useState('explore');
  const [responseInput, setResponseInput] = useState('');
  const [streamingContent, setStreamingContent] = useState({});
  const messagesEndRef = useRef(null);
  // 成员调整：待删除的 id 列表、待添加的 {name, reason} 列表
  const [adjustmentsToRemove, setAdjustmentsToRemove] = useState([]);
  const [adjustmentsToAdd, setAdjustmentsToAdd] = useState([]);
  const [addRoleInput, setAddRoleInput] = useState('');
  const [phaseSummaries, setPhaseSummaries] = useState({});
  const [stanceEvolution, setStanceEvolution] = useState([]);
  const [bottomInfoExpanded, setBottomInfoExpanded] = useState(false);
  const [stanceExpanded, setStanceExpanded] = useState(false);
  // 历史记录相关状态
  const [viewMode, setViewMode] = useState('new'); // 'new' | 'history' | 'replay'
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [replayArchive, setReplayArchive] = useState(null);

  const getLanguage = () => getPreferredLanguage();

  // 发言气泡展示身份称谓（优先消息字段，回退成员表）
  const getAgentDisplayTitle = useCallback((msg) => {
    if (msg?.displayTitle) return msg.displayTitle;
    const byId = msg?.agentId && panelists.find((p) => p.id === msg.agentId);
    if (byId) return byId.display_title || byId.category || '';
    const byName = msg?.agentName && panelists.find((p) => p.name === msg.agentName);
    if (byName) return byName.display_title || byName.category || '';
    return '';
  }, [panelists]);

  const getVoteDisplayTitle = useCallback((vote) => {
    if (vote?.display_title) return vote.display_title;
    const byId = vote?.agent_id && panelists.find((p) => p.id === vote.agent_id);
    if (byId) return byId.display_title || byId.category || '';
    const byName = vote?.agent_name && panelists.find((p) => p.name === vote.agent_name);
    if (byName) return byName.display_title || byName.category || '';
    return '';
  }, [panelists]);

  // 总结类内容：压缩多余空行，减少渲染空白
  const normalizeSummaryContent = (text) => (text || '').replace(/\n{3,}/g, '\n\n').trim();

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // 连接WebSocket
  const connectWs = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(DISCUSS_HUB_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[DiscussHub] WebSocket已连接');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWsMessage(data);
    };

    ws.onclose = () => {
      console.log('[DiscussHub] WebSocket已断开');
    };

    ws.onerror = (err) => {
      console.error('[DiscussHub] WebSocket错误:', err);
    };
  }, []);

  // 处理WebSocket消息
  const handleWsMessage = useCallback((data) => {
    switch (data.type) {
      case 'discussion_created':
        setSessionId(data.sessionId);
        break;

      case 'panelists_proposed':
        if (data.sessionId) setSessionId(data.sessionId);
        setTopicSummary(data.topicSummary || '');
        setPanelists(data.panelists || []);
        setAdjustmentsToRemove([]);
        setAdjustmentsToAdd([]);
        setAddRoleInput('');
        setStatus('preparing');
        setIsWaiting(false);
        break;

      case 'system_message':
        setMessages(prev => [...prev, {
          type: 'system',
          content: data.content,
        }]);
        break;

      case 'panelists_confirmed':
        if (data.sessionId) setSessionId(data.sessionId);
        setPanelists(data.panelists || panelists);
        setStatus('active');
        setIsWaiting(false);
        // 提示学生输入初始立场
        setMessages(prev => [...prev, {
          type: 'system',
          content: data.message || t('discuss.panelists_confirmed'),
        }]);
        break;

      case 'phase_change':
        setPhase(data.phase);
        setMessages(prev => [...prev, {
          type: 'phase',
          phase: data.phase,
          content: data.phaseDesc,
        }]);
        break;

      case 'agent_speech':
        setMessages(prev => [...prev, {
          type: 'agent',
          agentId: data.agentId,
          agentName: data.agentName,
          displayTitle: data.displayTitle,
          content: data.content,
          speechType: data.speechType,
          phase: data.phase,
        }]);
        break;

      case 'agent_speech_start':
        setStreamingContent(prev => ({
          ...prev,
          [data.agentId]: { name: data.agentName, displayTitle: data.displayTitle, content: '' },
        }));
        break;

      case 'agent_speech_chunk':
        setStreamingContent(prev => ({
          ...prev,
          [data.agentId]: {
            ...prev[data.agentId],
            content: (prev[data.agentId]?.content || '') + data.chunk,
          },
        }));
        break;

      case 'agent_speech_end':
        // 流式结束，移到正式消息
        setStreamingContent(prev => {
          const next = { ...prev };
          delete next[data.agentId];
          return next;
        });
        setMessages(prev => [...prev, {
          type: 'agent',
          agentId: data.agentId,
          agentName: data.agentName,
          displayTitle: data.displayTitle,
          content: data.content,
          speechType: data.speechType,
          replyTo: data.replyTo,
          phase: phase,
        }]);
        break;

      case 'moderator_question':
        setMessages(prev => [...prev, {
          type: 'moderator',
          content: data.content,
          phase: data.phase,
        }]);
        setIsWaiting(false);
        break;

      case 'phase_summary':
        setPhaseSummaries(prev => ({ ...prev, [data.phase]: data.summary }));
        if (data.stanceEvolution) setStanceEvolution(data.stanceEvolution);
        setMessages(prev => [...prev, {
          type: 'summary',
          phase: data.phase,
          content: data.summary,
          nextPhase: data.nextPhaseDesc,
        }]);
        break;

      case 'vote_result':
        setMessages(prev => [...prev, {
          type: 'vote',
          votes: data.votes,
          options: data.options,
          counts: data.counts,
          summary: data.summary,
          phase: data.phase,
        }]);
        break;

      case 'discussion_ended':
        if (data.stanceEvolution) setStanceEvolution(data.stanceEvolution);
        setMessages(prev => [...prev, {
          type: 'final',
          content: data.summary,
        }]);
        setStatus('completed');
        setIsWaiting(false);
        break;

      case 'archive_saved':
        setMessages(prev => [...prev, {
          type: 'system',
          content: '✅ 存档保存成功！可在"我的研讨记录"中查看。',
        }]);
        break;

      case 'error':
        setMessages(prev => [...prev, {
          type: 'error',
          content: data.message,
        }]);
        setIsWaiting(false);
        break;

      default:
        console.log('[DiscussHub] 未处理的消息类型:', data.type);
    }
  }, [phase, panelists, t]);

  // 发送消息
  const sendWsMessage = useCallback((msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // 创建研讨（参数与 AgentChat/ExpertService 严格对齐）
  const handleCreateDiscussion = () => {
    if (!topicInput.trim()) return;
    connectWs();
    // userId/userType 与 AgentChat 一致，确保 token 记录与额度拦截正确
    const userId = String(currentUser?.unified_user_id || currentUser?.userId || currentUser?.studentID || currentUser?.student_id || currentUser?.username || 'anonymous');
    const userType = currentUser?.user_type || (currentUser?.isSchoolUser ? 'campus' : currentUser?.isCasUser ? 'bupt_cas' : 'guest');
    // 等WebSocket连接后发送
    const checkAndSend = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        sendWsMessage({
          type: 'create_discussion',
          topic: topicInput.trim(),
          topicType: topicType,
          studentId: userId,
          username: currentUser?.username ?? userId,
          userType: userType,
          language: getLanguage(),
        });
        setIsWaiting(true);
        setMessages([{ type: 'system', content: t('discuss.analyzing_topic', { topic: topicInput.trim() }) }]);
      } else {
        setTimeout(checkAndSend, 200);
      }
    };
    setTimeout(checkAndSend, 300);
  };

  // 确认角色（携带增删调整）
  const handleConfirmPanelists = () => {
    sendWsMessage({
      type: 'confirm_panelists',
      sessionId: sessionId,
      adjustments: {
        add: adjustmentsToAdd.map(({ name, reason }) => ({ name, reason })),
        remove: adjustmentsToRemove,
      },
    });
    setIsWaiting(true);
  };

  // 删除成员
  const handleRemovePanelist = (id) => {
    setAdjustmentsToRemove(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  // 撤销删除
  const handleUndoRemove = (id) => {
    setAdjustmentsToRemove(prev => prev.filter(x => x !== id));
  };

  // 添加成员
  const handleAddRole = () => {
    const text = addRoleInput.trim();
    if (!text) return;
    setAdjustmentsToAdd(prev => [...prev, { name: text, reason: text }]);
    setAddRoleInput('');
  };

  // 学生回应
  const handleStudentResponse = () => {
    if (!responseInput.trim()) return;
    sendWsMessage({
      type: 'student_response',
      sessionId: sessionId,
      content: responseInput.trim(),
    });
    setMessages(prev => [...prev, {
      type: 'student',
      content: responseInput.trim(),
    }]);
    setResponseInput('');
    setIsWaiting(true);
  };

  // 学生控制
  const handleControl = (action) => {
    sendWsMessage({
      type: 'student_control',
      sessionId: sessionId,
      action: action,
    });
  };

  // 新研讨
  const handleNewDiscussion = () => {
    onReset();
    setTopicInput('');
    setTopicType('explore');
    setResponseInput('');
    setStreamingContent({});
    setPhaseSummaries({});
    setStanceEvolution([]);
    setBottomInfoExpanded(false);
    setStanceExpanded(false);
    setViewMode('new');
  };

  // 加载历史记录列表
  const loadHistoryList = useCallback(async () => {
    const userId = String(currentUser?.unified_user_id || currentUser?.userId || currentUser?.studentID || currentUser?.student_id || currentUser?.username || 'anonymous');
    if (!userId || userId === 'anonymous') {
      console.warn('[DiscussHub] 无法加载历史记录：用户未登录');
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await fetch(`${DISCUSS_HUB_HTTP_URL}/discussion/getList?studentId=${encodeURIComponent(userId)}&page=1&pageSize=50`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setHistoryList(data.items || []);
    } catch (error) {
      console.error('[DiscussHub] 加载历史记录失败:', error);
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [currentUser]);

  // 加载详情并切换到回放视图
  const loadDetailAndReplay = useCallback(async (sessionId) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${DISCUSS_HUB_HTTP_URL}/discussion/getDetail?sessionId=${encodeURIComponent(sessionId)}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const archive = await response.json();
      setReplayArchive(archive);
      setViewMode('replay');

      // 转换 dialogs 为 messages 格式
      const convertedMessages = [];
      if (archive.dialogs) {
        archive.dialogs.forEach(d => {
          if (d.speaker_type === 'student') {
            convertedMessages.push({ type: 'student', content: d.content });
          } else if (d.speaker_type === 'moderator') {
            convertedMessages.push({ type: 'moderator', content: d.content });
          } else if (d.speaker_type === 'agent') {
            convertedMessages.push({
              type: 'agent',
              agentId: d.speaker_id,
              agentName: d.speaker_name,
              displayTitle: d.speaker_display_title,
              content: d.content,
            });
          }
        });
      }
      setMessages(convertedMessages);

      // 设置阶段小结
      if (archive.phase_summaries) {
        setPhaseSummaries(archive.phase_summaries);
      }

      // 设置立场轨迹
      if (archive.stance_evolution) {
        setStanceEvolution(archive.stance_evolution);
      }

      // 设置成员
      if (archive.panelists) {
        setPanelists(archive.panelists);
      }

      // 设置议题摘要
      if (archive.topic_summary) {
        setTopicSummary(archive.topic_summary);
      }

      setStatus('completed');
    } catch (error) {
      console.error('[DiscussHub] 加载详情失败:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // 从回放视图保存为 training_archives
  const handleSaveFromHistory = useCallback(async () => {
    if (!replayArchive?.session_id) return;
    const username = currentUser?.username ?? currentUser?.unified_user_id ?? currentUser?.userId ?? 'anonymous';
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${DISCUSS_HUB_HTTP_URL}/discussion/saveToTrainingArchives?sessionId=${encodeURIComponent(replayArchive.session_id)}&username=${encodeURIComponent(username)}`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setMessages(prev => [...prev, { type: 'system', content: '✅ ' + (data.message || '存档保存成功！可在训练存档列表中查看。') }]);
      } else {
        setMessages(prev => [...prev, { type: 'error', content: data.message || '保存失败' }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { type: 'error', content: err.message || '保存失败' }]);
    } finally {
      setHistoryLoading(false);
    }
  }, [replayArchive, currentUser]);

  // 切换到历史记录视图
  useEffect(() => {
    if (viewMode === 'history' && historyList.length === 0 && !historyLoading) {
      loadHistoryList();
    }
  }, [viewMode, historyList.length, historyLoading, loadHistoryList]);

  if (!isOpen) return null;

  const brandThemeStyle = buildAccentThemeStyle(currentUser?.branding, 'dp');

  const phaseDescMap = {
    init: t('discuss.phase_init'),
    discover: t('discuss.phase_discover'),
    define: t('discuss.phase_define'),
    develop: t('discuss.phase_develop'),
    deliver: t('discuss.phase_deliver'),
    summary: t('discuss.phase_summary_badge'),
  };

  return (
    <div className="discuss-panel-fullscreen" style={brandThemeStyle}>
        {/* 头部 */}
        <div className="discuss-header">
          <div className="discuss-header-left">
            <button className="discuss-back-btn" onClick={onClose}>
              {t('discuss.back')}
            </button>
            <h2>{t('discuss.title')}</h2>
            {topicSummary && <span className="discuss-topic">{topicSummary}</span>}
            {/* Tab 切换 */}
            {viewMode !== 'replay' && (
              <div className="discuss-tabs" style={{ marginLeft: '20px', display: 'flex', gap: '10px' }}>
                <button
                  className={viewMode === 'new' ? 'active' : ''}
                  onClick={() => setViewMode('new')}
                  style={{
                    padding: '6px 16px',
                    border: 'none',
                    background: viewMode === 'new' ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('discuss.new_tab')}
                </button>
                <button
                  className={viewMode === 'history' ? 'active' : ''}
                  onClick={() => setViewMode('history')}
                  style={{
                    padding: '6px 16px',
                    border: 'none',
                    background: viewMode === 'history' ? '#38bdf8' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('discuss.my_records')}
                </button>
              </div>
            )}
            {viewMode === 'replay' && (
              <>
                <button
                  onClick={handleSaveFromHistory}
                  disabled={historyLoading}
                  style={{
                    marginLeft: '20px',
                    padding: '6px 16px',
                    border: 'none',
                    background: 'rgba(251,191,36,0.2)',
                    color: '#fbbf24',
                    borderRadius: '6px',
                    cursor: historyLoading ? 'wait' : 'pointer'
                  }}
                >
                  {historyLoading ? t('discuss.loading') : t('discuss.save_archive_from_history')}
                </button>
                <button
                  onClick={() => {
                    setViewMode('history');
                    setReplayArchive(null);
                    setMessages([]);
                    setPhaseSummaries({});
                    setStanceEvolution([]);
                    setPanelists([]);
                    setTopicSummary('');
                  }}
                  style={{
                    marginLeft: '8px',
                    padding: '6px 16px',
                    border: 'none',
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  {t('discuss.back_to_list')}
                </button>
              </>
            )}
          </div>
          <div className="discuss-header-right">
            {phase !== 'init' && (
              <div className="discuss-phase-progress">
                <div className="phase-progress-bar">
                  {['discover', 'define', 'develop', 'deliver', 'summary'].map((p, i) => {
                    const phaseOrder = ['discover', 'define', 'develop', 'deliver', 'summary'];
                    const currentIdx = phaseOrder.indexOf(phase);
                    const isActive = currentIdx === i;
                    const isPast = currentIdx > i || (phase === 'summary' && i < 5);
                    return (
                      <div
                        key={p}
                        className={`phase-segment ${isPast ? 'done' : ''} ${isActive ? 'active' : ''}`}
                        title={phaseDescMap[p]}
                      />
                    );
                  })}
                </div>
                <span className="phase-progress-label">{phaseDescMap[phase] || phase}</span>
              </div>
            )}
          </div>
        </div>

        {/* 主体 */}
        <div className="discuss-body">
          {/* 历史记录列表视图 */}
          {viewMode === 'history' && (
            <div className="discuss-history-list" style={{ width: '100%', padding: '20px', overflowY: 'auto' }}>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  {t('discuss.loading')}
                </div>
              ) : historyList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  {t('discuss.no_history')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {historyList.map((item) => (
                    <div
                      key={item.sessionId}
                      onClick={() => item.status === 'completed' && loadDetailAndReplay(item.sessionId)}
                      style={{
                        padding: '16px',
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        cursor: item.status === 'completed' ? 'pointer' : 'default',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (item.status === 'completed') {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                          e.currentTarget.style.borderColor = 'rgba(56,189,248,0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '16px', fontWeight: 500, color: '#e2e8f0', marginBottom: '8px' }}>
                            {item.topic && item.topic.length > 60 ? item.topic.slice(0, 60) + '...' : item.topic}
                          </div>
                          <div style={{ display: 'flex', gap: '12px', fontSize: '14px', color: '#94a3b8' }}>
                            <span>
                              {item.status === 'completed' ? t('discuss.status_completed') : item.status === 'active' ? t('discuss.status_active') : item.status === 'abandoned' ? t('discuss.status_abandoned') : item.status}
                            </span>
                            {item.totalRounds > 0 && <span>• {t('discuss.rounds_count', { count: item.totalRounds })}</span>}
                            {item.completedAt && (
                              <span>• {new Date(item.completedAt).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        {item.status === 'completed' && (
                          <span style={{ color: '#38bdf8', fontSize: '14px' }}>{t('discuss.view_detail')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 新建研讨视图或回放视图 */}
          {(viewMode === 'new' || viewMode === 'replay') && (
            <>
          {/* 左侧：成员面板 */}
          {panelists.length > 0 && (
            <div className="discuss-sidebar">
              <h3>{t('discuss.panelists')}</h3>
              {panelists
                .filter(p => !adjustmentsToRemove.includes(p.id))
                .map((p, i) => (
                  <div key={p.id || i} className="discuss-panelist-card">
                    <span className="panelist-icon">{p.icon || '👤'}</span>
                    <div className="panelist-info">
                      <span className="panelist-name">{p.name}</span>
                      <span className="panelist-category">{p.display_title || p.category}</span>
                      {status === 'active' && p.initial_stance && (
                        <span className="panelist-stance" title={p.initial_stance}>
                          {p.initial_stance.length > 24 ? p.initial_stance.slice(0, 24) + '…' : p.initial_stance}
                        </span>
                      )}
                    </div>
                    {status === 'preparing' && (
                      <button
                        className="panelist-remove-btn"
                        onClick={() => handleRemovePanelist(p.id)}
                        title={t('discuss.remove_panelist')}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              {adjustmentsToRemove.length > 0 && status === 'preparing' && (
                <div className="adjustments-removed">
                  {panelists
                    .filter(p => adjustmentsToRemove.includes(p.id))
                    .map(p => (
                      <span key={p.id} className="removed-tag">
                        {p.name}
                        <button onClick={() => handleUndoRemove(p.id)}>↩</button>
                      </span>
                    ))}
                </div>
              )}
              {status === 'preparing' && (
                <>
                  <div className="add-role-area">
                    <input
                      type="text"
                      value={addRoleInput}
                      onChange={(e) => setAddRoleInput(e.target.value)}
                      placeholder={t('discuss.add_role_placeholder')}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRole(); } }}
                    />
                    <button className="add-role-btn" onClick={handleAddRole} disabled={!addRoleInput.trim()}>
                      {t('discuss.add_role')}
                    </button>
                  </div>
                  {adjustmentsToAdd.length > 0 && (
                    <div className="adjustments-added">
                      {adjustmentsToAdd.map((a, i) => (
                        <span key={i} className="added-tag">
                          + {a.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <button className="discuss-confirm-btn" onClick={handleConfirmPanelists} disabled={isWaiting}>
                    {isWaiting ? t('discuss.waiting') : t('discuss.confirm_panelists')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* 右侧：对话区 */}
          <div className="discuss-chat">
            <div className="discuss-messages">
              {/* 初始状态：输入议题 */}
              {status === 'idle' && (
                <div className="discuss-welcome">
                  <h3>{t('discuss.welcome_title')}</h3>
                  <p>{t('discuss.welcome_desc')}</p>
                  <div className="discuss-topic-type-selector">
                    <div
                      className={`topic-type-card ${topicType === 'explore' ? 'active' : ''}`}
                      onClick={() => setTopicType('explore')}
                    >
                      <span className="topic-type-icon">🔭</span>
                      <span className="topic-type-name">工程科学问题探索</span>
                      <span className="topic-type-desc">深度思辨、多维约束、可行性辩论</span>
                    </div>
                    <div
                      className={`topic-type-card ${topicType === 'project' ? 'active' : ''}`}
                      onClick={() => setTopicType('project')}
                    >
                      <span className="topic-type-icon">🚀</span>
                      <span className="topic-type-name">项目打磨</span>
                      <span className="topic-type-desc">双创选题、需求分析、idea验证</span>
                    </div>
                    <div
                      className={`topic-type-card ${topicType === 'decision' ? 'active' : ''}`}
                      onClick={() => setTopicType('decision')}
                    >
                      <span className="topic-type-icon">⚖️</span>
                      <span className="topic-type-name">方案决策</span>
                      <span className="topic-type-desc">技术选型、架构设计、方案对比</span>
                    </div>
                  </div>
                  <div className="discuss-topic-input-area">
                    <textarea
                      value={topicInput}
                      onChange={(e) => setTopicInput(e.target.value)}
                      placeholder={
                        topicType === 'explore' ? '输入你想分析的工程科学问题，如：校园二手交易平台如何识别真实需求并设计最小可行产品？' :
                        topicType === 'project' ? '描述你的项目/idea，如：我想做一个校园二手交易平台，帮助同学们低价买卖闲置物品' :
                        '描述你面临的决策，如：我们团队在React和Vue之间犹豫，项目是一个中型管理后台'
                      }
                      rows={3}
                    />
                    <button
                      onClick={handleCreateDiscussion}
                      disabled={!topicInput.trim() || isWaiting}
                    >
                      {isWaiting ? t('discuss.analyzing') : t('discuss.start')}
                    </button>
                  </div>
                </div>
              )}

              {/* 消息列表 */}
              {messages.map((msg, i) => (
                <div key={i} className={`discuss-msg discuss-msg-${msg.type}`}>
                  {msg.type === 'system' && (
                    <div className="msg-system">{msg.content}</div>
                  )}
                  {msg.type === 'phase' && (
                    <div className="msg-phase">
                      <span className="phase-divider">── {msg.content} ──</span>
                    </div>
                  )}
                  {msg.type === 'agent' && (
                    <div className="msg-agent">
                      <div className="msg-agent-top-row">
                        <div className="msg-agent-identity">
                          <span className="msg-agent-name">{msg.agentName}</span>
                          {getAgentDisplayTitle(msg) && (
                            <span className="msg-agent-title">{getAgentDisplayTitle(msg)}</span>
                          )}
                        </div>
                        {((msg.speechType && msg.speechType !== 'response') || msg.replyTo) && (
                          <div className="msg-agent-meta">
                            {msg.speechType && msg.speechType !== 'response' && (
                              <span className={`msg-speech-type st-${msg.speechType}`}>
                                {msg.speechType === 'statement' ? t('discuss.speech_statement') : t('discuss.speech_supplement')}
                              </span>
                            )}
                            {msg.replyTo && (
                              <span className="msg-reply-to">{t('discuss.reply_to', { name: msg.replyTo === '学生' ? t('discuss.student_label') : msg.replyTo })}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="msg-agent-content"><EnhancedMarkdown content={msg.content || ''} /></div>
                    </div>
                  )}
                  {msg.type === 'moderator' && (
                    <div className="msg-moderator">
                      <span className="msg-moderator-label">{t('discuss.moderator_label')}</span>
                      <div className="msg-moderator-content"><EnhancedMarkdown content={msg.content || ''} /></div>
                      <div className="msg-moderator-hint">{t('discuss.respond_hint')}</div>
                    </div>
                  )}
                  {msg.type === 'student' && (
                    <div className="msg-student">
                      <span className="msg-student-label">{t('discuss.my_response')}</span>
                      <div className="msg-student-content"><EnhancedMarkdown content={msg.content || ''} /></div>
                    </div>
                  )}
                  {msg.type === 'summary' && (
                    <div className="msg-summary">
                      <span className="msg-summary-label">{t('discuss.phase_summary')}</span>
                      <div className="msg-summary-content"><EnhancedMarkdown content={normalizeSummaryContent(msg.content)} compactMode /></div>
                      {msg.nextPhase && <div className="msg-next-phase">{t('discuss.entering_phase')}{msg.nextPhase}</div>}
                    </div>
                  )}
                  {msg.type === 'vote' && (
                    <div className="msg-vote">
                      <span className="msg-vote-label">{t('discuss.vote_result')}</span>
                      {msg.options?.length > 0 && (
                        <div className="vote-options-list">
                          <div className="vote-options-title">投票选项：</div>
                          {msg.options.map((opt, j) => {
                            const idx = j + 1;
                            const count = msg.counts?.[idx] || 0;
                            return (
                              <div key={j} className="vote-option-row">
                                <span className="vote-option-num">方案{idx}</span>
                                <span className="vote-option-text">{opt}</span>
                                <span className="vote-option-count">{count}票</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="vote-details-title">各方投票：</div>
                      {msg.votes?.map((v, j) => (
                        <div key={j} className="vote-item">
                          <span className="vote-item-name-block">
                            <span className="vote-item-name">{v.agent_name}</span>
                            {getVoteDisplayTitle(v) && (
                              <span className="vote-item-title">{getVoteDisplayTitle(v)}</span>
                            )}
                          </span>
                          <span className="vote-item-arrow">→</span>
                          <span className="vote-item-choice">
                            {v.choice_idx > 0 ? `方案${v.choice_idx}「${v.choice_text}」` : '未明确选择'}
                          </span>
                          {v.reason && v.reason !== v.choice && (
                            <div className="vote-item-reason">{v.reason}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.type === 'final' && (
                    <div className="msg-final">
                      <span className="msg-final-label">{t('discuss.final_summary')}</span>
                      <div className="msg-final-content"><EnhancedMarkdown content={normalizeSummaryContent(msg.content)} compactMode /></div>
                    </div>
                  )}
                  {msg.type === 'error' && (
                    <div className="msg-error">⚠️ {msg.content}</div>
                  )}
                </div>
              ))}

              {/* 流式输出中的Agent */}
              {Object.entries(streamingContent).map(([agentId, data]) => (
                <div key={agentId} className="discuss-msg discuss-msg-agent streaming">
                  <div className="msg-agent">
                    <div className="msg-agent-top-row">
                      <div className="msg-agent-identity">
                        <span className="msg-agent-name">{data.name}</span>
                        {(data.displayTitle || getAgentDisplayTitle({ agentId, agentName: data.name })) && (
                          <span className="msg-agent-title">
                            {data.displayTitle || getAgentDisplayTitle({ agentId, agentName: data.name })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="msg-agent-content">
                      <EnhancedMarkdown content={data.content || ''} isStreaming />
                      <span className="typing-cursor">▌</span>
                    </div>
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* 底部信息区：阶段小结 + 立场轨迹 并排 */}
            {(Object.keys(phaseSummaries).length > 0 || stanceEvolution.length > 0) && status !== 'idle' && (
              <div className="discuss-bottom-row">
                {/* 左侧：阶段小结 */}
                {Object.keys(phaseSummaries).length > 0 && (
                  <div className="discuss-bottom-panel">
                    <div className="bottom-panel-header" onClick={() => setBottomInfoExpanded(!bottomInfoExpanded)}>
                      <span>{t('discuss.latest_summary')}</span>
                      <span className="bottom-panel-toggle">{bottomInfoExpanded ? '▼' : '▶'}</span>
                    </div>
                    {bottomInfoExpanded && (
                      <div className="bottom-panel-body">
                        {Object.entries(phaseSummaries).map(([p, summary]) => (
                          <div key={p} className="bottom-phase-summary">
                            <span className="bottom-phase-label">{phaseDescMap[p] || p}</span>
                            <div className="bottom-phase-text"><EnhancedMarkdown content={normalizeSummaryContent(summary)} compactMode /></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* 右侧：立场轨迹（实时） */}
                {stanceEvolution.length > 0 && (
                  <div className="discuss-bottom-panel discuss-stance-panel">
                    <div className="bottom-panel-header" onClick={() => setStanceExpanded(!stanceExpanded)}>
                      <span>{t('discuss.stance_timeline_title')}</span>
                      <span className="bottom-panel-toggle">{stanceExpanded ? '▼' : '▶'}</span>
                    </div>
                    {stanceExpanded && (
                      <div className="bottom-panel-body">
                        {stanceEvolution.map((sc, i) => (
                          <div key={i} className="stance-timeline-item">
                            <span className="stance-phase-tag">{phaseDescMap[sc.phase] || sc.phase}</span>
                            <span className="stance-text">{sc.stance}</span>
                            {sc.trigger && sc.trigger !== 'LLM_analysis' && sc.trigger !== 'initial_stance' && sc.trigger !== 'student_response' && sc.trigger !== 'final_reflection' && (
                              <span className="stance-trigger">← {sc.trigger}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 输入区（回放视图不显示） */}
            {(status === 'active' && viewMode !== 'replay') && (
              <div className="discuss-input-area">
                <div className="discuss-controls">
                  <button onClick={() => handleControl('next_phase')} title={t('discuss.next_phase_title')}>
                    {t('discuss.next_phase')}
                  </button>
                  <button onClick={() => handleControl('end')} title={t('discuss.end_title')}>
                    {t('discuss.end')}
                  </button>
                  <button
                    onClick={() => {
                      sendWsMessage({ type: 'save_archive', sessionId });
                      setMessages(prev => [...prev, { type: 'system', content: '💾 正在保存存档...' }]);
                    }}
                    title="保存当前研讨进度为存档"
                    style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
                  >
                     {t('discuss.save_archive') || '保存存档'}
                  </button>
                </div>
                <div className="discuss-response-input">
                  <textarea
                    value={responseInput}
                    onChange={(e) => setResponseInput(e.target.value)}
                    placeholder={t('discuss.input_placeholder')}
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleStudentResponse();
                      }
                    }}
                  />
                  <button
                    onClick={handleStudentResponse}
                    disabled={!responseInput.trim() || isWaiting}
                  >
                    {isWaiting ? t('discuss.waiting') : t('discuss.send')}
                  </button>
                </div>
              </div>
            )}

            {/* 完成状态 */}
            {status === 'completed' && (
              <div className="discuss-completed">
                <button onClick={handleNewDiscussion}>{t('discuss.new_discussion')}</button>
              </div>
            )}
          </div>
            </>
          )}
        </div>
    </div>
  );
};

export default DiscussionPanel;
